'use client';

import { ReviewEntity } from '@/entities/review.entity';
import { ChatMessage } from '@/contracts/marketplace/chat-message';
import type { ReviewAnswerDraftEntity } from '@/contracts/marketplace/auto-replies';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useSocketIo } from '@/context/socket-io.context';
import { useAuth } from '@/context/auth.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { createClientLogger, logClientError, logClientWarning } from '@/client/logging/client-logger';

type ChatContextType = {
    review: ReviewEntity | undefined;
    setReview?: (review: ReviewEntity) => void;
    history: ChatMessage[];
    setHistory: (history: ChatMessage[]) => void;
    pendingAnswerDraft: ReviewAnswerDraftEntity | null;
    setPendingAnswerDraft: (draft: ReviewAnswerDraftEntity | null) => void;
};

type NonNullContextType = Omit<ChatContextType, 'review'> & { review: ReviewEntity };

type ProviderProps = {
    review: ReviewEntity;
    history: ChatMessage[];
    pendingAnswerDraft?: ReviewAnswerDraftEntity | null;
    children: ReactNode;
};

const ChatContext = createContext<ChatContextType>({
    review: undefined,
    setReview: () => {},
    history: [],
    setHistory: () => {},
    pendingAnswerDraft: null,
    setPendingAnswerDraft: () => {},
});

const chatContextLogger = createClientLogger({
    scope: 'chat-context',
});

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context.review) throw new Error('Review not initialized');
    return context as NonNullContextType;
};

export const ChatContextProvider = ({ children, review, history, pendingAnswerDraft = null }: ProviderProps) => {
    const [_review, setReview] = useState<ReviewEntity>(review);
    const [_history, setHistory] = useState<ChatMessage[]>(history);
    const [_pendingAnswerDraft, setPendingAnswerDraft] = useState<ReviewAnswerDraftEntity | null>(pendingAnswerDraft);
    const { reviewsApi } = useMarketplaceApi();

    const { events } = useSocketIo();
    const { accessToken } = useAuth();

    useEffect(() => {
        const refreshActiveReview = async (updatedReview: ReviewEntity) => {
            if (updatedReview.id !== review.id) return;

            try {
                const [historyResponse, draft] = await Promise.all([
                    reviewsApi.getHistory(review.id, { authToken: accessToken }),
                    (async (): Promise<ReviewAnswerDraftEntity | null> => {
                        try {
                            const draftResponse = await reviewsApi.getAnswerDraft(review.id, {
                                authToken: accessToken,
                            });
                            return draftResponse.data;
                        } catch (error) {
                            logClientWarning({
                                logger: chatContextLogger,
                                message: '[client.chat.answer-draft-refresh] Failed to refresh pending answer draft.',
                                error,
                                context: {
                                    reviewId: review.id,
                                },
                            });
                            return null;
                        }
                    })(),
                ]);

                setReview(updatedReview);
                setHistory(historyResponse.data);
                setPendingAnswerDraft(draft);
            } catch (error) {
                logClientError({
                    logger: chatContextLogger,
                    message: '[client.chat.history-refresh] Failed to refresh review chat history.',
                    error,
                    context: {
                        reviewId: review.id,
                    },
                });
            }
        };

        const unsubscribeAnswerJobDone = events.on('reviewAnswerJobDone', ({ review: updatedReview }) => {
            void refreshActiveReview(updatedReview);
        });
        const unsubscribeReviewChanged = events.on('reviewFeedItemChanged', ({ review: updatedReview }) => {
            void refreshActiveReview(updatedReview);
        });

        return () => {
            unsubscribeAnswerJobDone();
            unsubscribeReviewChanged();
        };
    }, [accessToken, events, review.id, reviewsApi]);

    return (
        <ChatContext.Provider
            value={{
                review: _review,
                history: _history,
                setReview,
                setHistory,
                pendingAnswerDraft: _pendingAnswerDraft,
                setPendingAnswerDraft,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};
